import {
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css'
import 'react-calendar/dist/Calendar.css'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { CustomInput, CustomSelect, selectTypes } from 'src/consumer/components/helperComponents'
import { useDateRangeLogic } from '../hooks/useDateRangeLogic'
import {
  collectAllReportsData,
  downloadPdfReports,
  emailPdfReports,
} from '../services/exportServices'
import { EXPORT_TYPES } from '../util'
import { generateExcelReport } from '../utils/excelGenerators'
import { validateEmail } from '../utils/validationHelpers'
import DateRangeSelector from './DateRangeSelector'

export default function ExportPayoutReports({
  startDate,
  endDate,
  dataFetchingApiUrl,
  extraParams = {},
  payoutReportDataArrayToExcelFormatArrayBuilder,
  fileNamePrefix = 'Payout Reports',
  closeDialog,
  entityId,
  entityType,
  useLocalDateFormatForApiCalls = false,
  eventType,
  allEventTypes,
  exportType = EXPORT_TYPES.excel.id,
}) {
  const {
    dateRangeMode,
    setDateRangeMode,
    dialogStartDate,
    setDialogStartDate,
    customDateRange,
    setCustomDateRange,
    getReportDateRange,
    isValidDateRange,
    generateFileName,
  } = useDateRangeLogic(startDate, endDate)

  const [isFetching, setIsFetching] = useState(false)
  const [loadingPercentage, setLoadingPercentage] = useState(0)
  const [exportEventType, setExportEventType] = useState(eventType)
  const [emailToSendTo, setEmailToSendTo] = useState('')
  const [sendRazorpayNotifications, setSendRazorpayNotifications] = useState(false)
  const [sendRazorpayReminderNotifications, setSendRazorpayReminderNotifications] = useState(false)
  const [addTdsRelatedInfo, setAddTdsRelatedInfo] = useState(false)
  const [includeAllSessionsInReport, setIncludeAllSessionsInReport] = useState(false)
  const [includeNonAumhumAffiliations, setIncludeNonAumhumAffiliations] = useState(false)
  const [billDate, setBillDate] = useState(dayjs())
  const [hideGeneratedOn, setHideGeneratedOn] = useState(false)

  const exportEventTypeObject = EXPORT_TYPES?.[exportType] || {}
  const isValidEmail = useMemo(
    () => validateEmail(emailToSendTo, exportType),
    [emailToSendTo, exportType],
  )

  async function downloadExcelReports({ reportDateMoment, reportEndDateMoment }) {
    const fetchParams = {
      reportDateMoment,
      reportEndDateMoment,
      dataFetchingApiUrl,
      extraParams,
      exportEventType,
      entityType,
      useLocalDateFormatForApiCalls,
      setLoadingPercentage,
    }

    const collectedData = await collectAllReportsData(fetchParams)
    if (!collectedData) return

    const transformedReportData = collectedData.map((data) =>
      payoutReportDataArrayToExcelFormatArrayBuilder(data),
    )

    const fileName = generateFileName(fileNamePrefix)
    generateExcelReport(transformedReportData, fileName)
  }
  async function handleActionButtonClick() {
    setIsFetching(true)
    setLoadingPercentage(0)

    const { start: reportDateMoment, end: reportEndDateMoment } = getReportDateRange

    try {
      switch (exportType) {
        case EXPORT_TYPES.excel.id:
          await downloadExcelReports({ reportDateMoment, reportEndDateMoment })
          break
        case EXPORT_TYPES.email_pdf.id:
          const reportsEmailResponse = await emailPdfReports({
            reportDateMoment,
            reportEndDateMoment,
            entityId,
            entityType,
            exportEventType,
            emailToSendTo,
            sendRazorpayNotifications,
            sendRazorpayReminderNotifications,
            addTdsRelatedInfo,
            includeAllSessionsInReport,
            billDate,
            hideGeneratedOn,
            includeNonAumhumAffiliations,
          })
          if (!reportsEmailResponse) return
          break
        case EXPORT_TYPES.pdf.id:
          const reportsPdfResponse = await downloadPdfReports({
            reportDateMoment,
            reportEndDateMoment,
            entityId,
            entityType,
            exportEventType,
            sendRazorpayNotifications,
            sendRazorpayReminderNotifications,
            addTdsRelatedInfo,
            includeAllSessionsInReport,
            billDate,
            hideGeneratedOn,
            includeNonAumhumAffiliations,
          })
          if (!reportsPdfResponse) return
          break
        default:
          return
      }
    } catch (error) {
      toast.error('Something went wrong while preparing the reports.')
      console.error(error)
    } finally {
      setIsFetching(false)
    }
  }

  return (
    <>
      <DialogTitle>Export Reports ({exportEventTypeObject?.label})</DialogTitle>
      <DialogContent>
        <div className="d-flex flex-column gap-3">
          <DateRangeSelector
            dateRangeMode={dateRangeMode}
            setDateRangeMode={setDateRangeMode}
            dialogStartDate={dialogStartDate}
            setDialogStartDate={setDialogStartDate}
            customDateRange={customDateRange}
            setCustomDateRange={setCustomDateRange}
            isFetching={isFetching}
            isValidDateRange={isValidDateRange}
          />
          {allEventTypes && allEventTypes.length > 0 && (
            <CustomSelect
              id="event-type"
              options={allEventTypes}
              value={exportEventType}
              getOptionLabel={(option) => option.title}
              getOptionValue={(option) => option.id}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 99999 }) }}
              onChangeEvent={(e) => setExportEventType(e.target.value)}
              label={'Event Type'}
              selectType={selectTypes.string}
            />
          )}
          
          <DatePicker
            label="Bill Date"
            value={billDate}
            onChange={(newValue) => setBillDate(newValue)}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: "outlined",
                size: "medium"
              }
            }}
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={hideGeneratedOn}
                onChange={(e) => setHideGeneratedOn(e.target.checked)}
              />
            }
            label="Hide Generated On"
          />
          {exportType === EXPORT_TYPES.email_pdf.id && (
            <CustomInput
              label="Email to send to"
              type="email"
              value={emailToSendTo}
              onChange={(e) => setEmailToSendTo(e.target.value)}
            />
          )}
          {(exportType === EXPORT_TYPES.email_pdf.id || exportType === EXPORT_TYPES.pdf.id) && (
            <div className="d-flex flex-column gap-1 mt-2">
              {entityType === 'mentor' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={addTdsRelatedInfo}
                      onChange={(e) => setAddTdsRelatedInfo(e.target.checked)}
                    />
                  }
                  label="Add TDS related info in the report"
                />
              )}
              {entityType === 'mentor' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeAllSessionsInReport}
                      onChange={(e) => setIncludeAllSessionsInReport(e.target.checked)}
                    />
                  }
                  label="Include all sessions in the report"
                />
              )}
              {entityType === 'clinic-brand' && (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeNonAumhumAffiliations}
                        onChange={(e) => setIncludeNonAumhumAffiliations(e.target.checked)}
                      />
                    }
                    label="Include clinic-associated practitioner sessions in PDF"
                  />
                  <Typography variant="caption" color="text.secondary">
                    These rows stay excluded from payout calculations and are added only as highlighted reference rows.
                  </Typography>
                </>
              )}
              <strong>Razorpay Notifications</strong>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendRazorpayNotifications}
                    onChange={(e) => setSendRazorpayNotifications(e.target.checked)}
                  />
                }
                label="Send Razorpay notifications"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendRazorpayReminderNotifications}
                    onChange={(e) => setSendRazorpayReminderNotifications(e.target.checked)}
                  />
                }
                label="Send Razorpay reminder notifications"
              />
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button color="unFocused" disabled={isFetching} onClick={closeDialog}>
          Cancel
        </Button>
        <div className="d-flex align-items-center gap-1">
          <Button
            variant="contained"
            disabled={isFetching || !isValidEmail || !isValidDateRange}
            onClick={handleActionButtonClick}
          >
            {isFetching ? 'Preparing Reports...' : exportEventTypeObject?.actionButtonText}
          </Button>
          {isFetching && (
            <CircularProgressWithLabel
              size="small"
              variant="determinate"
              value={loadingPercentage * 100}
            />
          )}
        </div>
      </DialogActions>
    </>
  )
}

function CircularProgressWithLabel(props) {
  return (
    <div style={{ position: 'relative', display: 'inline-flex', width: '30px' }}>
      <CircularProgress variant="determinate" {...props} sx={{ width: '100%' }} />
      <div
        style={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
          {`${Math.round(props.value)}%`}
        </Typography>
      </div>
    </div>
  )
}
